import {observable, action} from 'mobx';
import {observer, PropTypes as MobxPropTypes} from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import {UncontrolledTooltip} from 'reactstrap';

import inject from '../../../../hoc/injectHoc';
import ContentRequestModal from '../../../../modals/contentRequest/ContentRequestModal';

import {getNextRenewalText} from '../../../../../utils/creditsRenewalHelper';

import './contentRequestButton.scss';

/**
 * Content Request Button component.
 */
class ContentRequestButton extends React.Component {
  /**
   * Whether or not the modal is open.
   * @type {boolean}
   */
  @observable isContentRequestModalOpen = false;

  /**
   * Trigger when opening the modal.
   */
  @action onOpenModal = () => {
    this.isContentRequestModalOpen = true;
  };

  /**
   * Trigger when closing the modal.
   */
  @action onCloseModal = () => {
    this.isContentRequestModalOpen = false;
  };

  /**
   * Triggered when the user hovers over the button.
   *  Fetches the credits renewal info on-demand so we're not requesting it on every page load.
   */
  handleMouseEnter = () => {
    const {apiUserGetMeStore, apiCompanyCreditsRenewalStore} = this.props;

    const user = apiUserGetMeStore.getFulfilled();
    const companyId = user && user.projectContentCompany && user.projectContentCompany.id;

    if (companyId) {
      apiCompanyCreditsRenewalStore.refresh(companyId);
    }
  };

  /**
   * Renders the tooltip content, including the renewal info once it's loaded.
   *
   * @param {string} companyId
   * @returns {{}}
   */
  renderTooltipContent(companyId) {
    const {apiCompanyCreditsRenewalStore} = this.props;

    return apiCompanyCreditsRenewalStore.case(companyId, {
      'pre': () => 'Submit a Content Request',
      'pending': () => 'Submit a Content Request',
      'rejected': () => 'Submit a Content Request',
      'fulfilled': (creditsRenewal) => (
        <>
          <div>Submit a Content Request</div>
          <div style={{fontSize: 12}} className="mt-1">
            {getNextRenewalText(creditsRenewal)}
          </div>
        </>
      ),
    });
  }

  /**
   * Renders the component.
   *
   * @returns {{}}
   */
  render() {
    const {apiUserGetMeStore, isBadge} = this.props;

    return apiUserGetMeStore.case({
      'pre': () => null,
      'pending': () => null,
      'rejected': () => null,
      'fulfilled': (user) => {
        const companyId = user.projectContentCompany && user.projectContentCompany.id;

        const tooltip = (
          <UncontrolledTooltip placement="bottom" target="content-request-btn">
            {this.renderTooltipContent(companyId)}
          </UncontrolledTooltip>
        );

        const modal = (this.isContentRequestModalOpen) && (
          <ContentRequestModal
            isOpen={true}
            hasCredits={user.projectContentCompany && user.projectContentCompany.requestCredits > 0}
            planHasCredits={user.plan ? user.plan.requestCredits > 0 : false}
            onComplete={this.onCloseModal}
          />
        );

        if (isBadge) {
          return (
            <li className="nav-item badge-item content-request-button" id="badge-item-request">
              <button
                className="badge-item-btn"
                id="content-request-btn"
                type="button"
                onClick={this.onOpenModal}
                onMouseEnter={this.handleMouseEnter}
              >
                <span className="content-request-btn-icon">
                  {user.projectContentCompany && user.projectContentCompany.requestCredits}
                </span>

                <span className="badge-label">
                  Custom Requests
                </span>
              </button>

              {tooltip}
              {modal}
            </li>
          );
        }

        return (
          <div className="content-request-button">
            <button
              className="btn btn-light info-btn-responsive"
              id="content-request-btn"
              type="button"
              onClick={this.onOpenModal}
              onMouseEnter={this.handleMouseEnter}
            >
              Submit
            </button>

            {tooltip}
            {modal}
          </div>
        );
      }
    });
  }
}

ContentRequestButton.propTypes = {};

ContentRequestButton.propTypes = {
  isBadge: PropTypes.bool.isRequired,
  apiCompanyCreditsRenewalStore: MobxPropTypes.observableObject,
  apiUserGetMeStore: MobxPropTypes.observableObject,
};

ContentRequestButton.wrappedComponent = {};
ContentRequestButton.wrappedComponent.propTypes = {
  apiCompanyCreditsRenewalStore: MobxPropTypes.observableObject.isRequired,
  apiUserGetMeStore: MobxPropTypes.observableObject.isRequired,
};

export default inject(ContentRequestButton)(
  observer(ContentRequestButton)
);
